import type { ApiObject } from "@rudderstack/analytics-js";
import { z } from "zod";
import type { Accounting } from "../api/engage/engage.types";
import type { ShellUser } from "../api/types";
import type { Defined } from "../types";
import type { DeviceType, OsName, BrowserName } from "./deviceInformation";

export const trackEventPayloadZod = z.object({
  id: z.string(),
  name: z.string(),
  appName: z.string(),
  action: z
    .union([
      z.literal("click"),
      z.literal("hover"),
      z.literal("focus"),
      z.literal("page_view"),
      z.literal("scroll"),
    ])
    .optional(),
  type: z.string().optional(),
  experimentInfo: z
    .object({
      experimentId: z.string(),
      bucketId: z.string(),
    })
    .optional(),
  source: z.string().url(),
  purposes: z
    .union([
      z.literal("Functional"),
      z.literal("Marketing"),
      z.literal("Analytical"),
    ])
    .array()
    .optional(),
  specificInfo: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
    .optional(),
  customInfo: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
    .optional(),
  tag: z.string().optional(),
  target: z
    .union([
      z.literal("button"),
      z.literal("link"),
      z.literal("inputField"),
      z.literal("menu"),
      z.literal("tab"),
      z.literal("modal"),
    ])
    .optional(),
});

export type TrackEventPayload = z.infer<typeof trackEventPayloadZod>;

export const shouldEnforcePurpose = (appName: string, eventName: string) => {
  console.warn(
    `${appName} - ${eventName}: Events can't be sent without a clear purpose field. You have until the 30th september 2024 to add the field to your event.`
  );
  // 30th september 2024
  return 1727694538000 > Date.now();
};

export const trackEventZod = z.object({
  payload: trackEventPayloadZod,
});

export enum DataCollectors {
  Datadog,
  RudderStack,
}

export type TrackEvent = z.infer<typeof trackEventZod>;

export type ClientEvent = {
  eventInfo: {
    eventAction: Defined<TrackEvent["payload"]["action"]> | "";
    eventSessionId: string;
    eventTag: string;
    eventTarget: string;
    eventTimestamp: number;
    eventTracingId: TrackEvent["payload"]["id"];
    eventType: Defined<TrackEvent["payload"]["type"]>;
    eventValue: string;
  };
  userInfo: {
    userAccountId: Defined<Accounting["account_id"]>;
    userAccountName: Defined<Accounting["company"]>;
    userCountry: Defined<Accounting["company"]>;
    userId: ShellUser["id"];
    userLanguage: string;
    userProjectId: number;
    userRoles: string[];
    userTier: string;
    userTimezone: string;
  };
  deviceInfo: {
    deviceId: string;
    deviceType: DeviceType;
    deviceViewportSize: string;
    deviceOs: OsName;
    deviceOsVersion: string;
    deviceModel: string;
    deviceBrand: string;
    deviceBrowser: BrowserName;
    deviceBrowserVersion: string;
  };
  appInfo: {
    appName: string;
    appMfe: TrackEvent["payload"]["appName"];
    appVersion: string;
    appDomain: string;
  };
  pageInfo: {
    pageUrl: string;
    PrevPageURL: string;
  };
  experimentInfo: {
    experimentName: string;
    experimentBucket: string;
  };
  specificInfo:
    | TrackEvent["payload"]["customInfo"]
    | TrackEvent["payload"]["specificInfo"];
};

export type RudderStackClientEvent = {
  event: {
    tracing_id: TrackEvent["payload"]["id"];
    type: `com.sinch.onesaas.${string}`;
    source: "com.sinch.onesaas.frameworks";
    action?: TrackEvent["payload"]["action"] | "";
    target?: TrackEvent["payload"]["target"];
    name: string;
    tag_version?: string;
    timestamp: number;
    session_id: string;
  };
  user: {
    id: ShellUser["id"];
    anonymous_id: string;
    country: Defined<Accounting["country"]>;
    language: string;
    role: string[];
    timezone: string;
  };
  account: {
    id: string;
    tier: string;
    vendor_id: string;
  };
  device: {
    type: DeviceType;
    viewport_size: `${number}x${number}`;
    os: OsName;
    browser: BrowserName;
    browser_version: string;
    user_agent: string;
  };
  app: {
    name: "Onesaas";
    mfe: string;
    env: string;
  };
  page: {
    url: string;
    prev_url: string;
  };
  experiment: {
    name: string;
    bucket: string;
  };
  custom: ApiObject | undefined;
};
